import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../apiClient";
import { formatNumber } from "../formatNumber";

const chartWidth = 880;
const chartHeight = 320;
const chartPadding = { top: 24, right: 24, bottom: 44, left: 92 };
const rangeOptions = [
  { value: "all", label: "All time" },
  { value: "year", label: "Last year" },
  { value: "sixMonths", label: "Last 6 months" },
];

const getYAxisStep = (maxValue) => {
  if (maxValue <= 100000) return 10000;
  if (maxValue <= 500000) return 50000;
  return 100000;
};

const ReportsIncomeExpenditureTracking = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rangeScale, setRangeScale] = useState("all");

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const reportRes = await apiClient.get("/periods");
        setRows(Array.isArray(reportRes.data) ? reportRes.data : []);
      } catch (err) {
        setError("Failed to load income and expenditure tracking report.");
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, []);

  const chartData = useMemo(() => {
    const sortedRows = [...rows].sort((a, b) => {
      const left = String(a.DateEnd || "");
      const right = String(b.DateEnd || "");
      if (left !== right) return left.localeCompare(right);
      return Number(a.PeriodId || 0) - Number(b.PeriodId || 0);
    });

    const latestDate = sortedRows.reduce((latest, row) => {
      const date = row.DateEnd ? new Date(row.DateEnd) : null;
      if (!date || Number.isNaN(date.getTime())) return latest;
      return !latest || date > latest ? date : latest;
    }, null);

    const cutoffDate = latestDate && rangeScale !== "all" ? new Date(latestDate) : null;
    if (cutoffDate && rangeScale === "year") {
      cutoffDate.setFullYear(cutoffDate.getFullYear() - 1);
    }
    if (cutoffDate && rangeScale === "sixMonths") {
      cutoffDate.setMonth(cutoffDate.getMonth() - 6);
    }

    const visibleRows = cutoffDate
      ? sortedRows.filter((row) => {
          const date = row.DateEnd ? new Date(row.DateEnd) : null;
          return date && !Number.isNaN(date.getTime()) && date >= cutoffDate;
        })
      : sortedRows;

    const points = visibleRows.map((row) => ({
      label: row.Description || `Period ${row.PeriodId}`,
      credits: Number(row.IncomeAmount || 0),
      debits: Math.max(0, Number(row.ExpensesAmount || 0) - Number(row.SavingsAmount || 0)),
      savings: Number(row.SavingsAmount || 0),
      passiveIncome: Number(row.SavingsIncomeAmount || 0),
    }));

    while (
      points.length > 0 &&
      points[points.length - 1].credits === 0 &&
      points[points.length - 1].debits === 0 &&
      points[points.length - 1].savings === 0 &&
      points[points.length - 1].passiveIncome === 0
    ) {
      points.pop();
    }

    const rawMaxValue = points.reduce((max, point) => Math.max(max, point.credits, point.debits, point.savings, point.passiveIncome), 0);
    const yAxisStep = getYAxisStep(rawMaxValue);
    const maxValue = rawMaxValue > 0 ? Math.ceil(rawMaxValue / yAxisStep) * yAxisStep : yAxisStep;
    const innerWidth = chartWidth - chartPadding.left - chartPadding.right;
    const innerHeight = chartHeight - chartPadding.top - chartPadding.bottom;
    const stepX = points.length > 1 ? innerWidth / (points.length - 1) : 0;
    const scaleY = maxValue > 0 ? innerHeight / maxValue : 0;

    const makePoint = (value, index) => ({
      x: chartPadding.left + stepX * index,
      y: chartHeight - chartPadding.bottom - value * scaleY,
      value,
    });

    const creditPoints = points.map((point, index) => makePoint(point.credits, index));
    const debitPoints = points.map((point, index) => makePoint(point.debits, index));
    const savingsPoints = points.map((point, index) => makePoint(point.savings, index));
    const passiveIncomePoints = points.map((point, index) => makePoint(point.passiveIncome, index));

    const toPath = (pts) => pts.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`).join(" ");

    const tickValues = [];
    for (let tick = maxValue; tick >= 0; tick -= yAxisStep) {
      tickValues.push(tick);
    }

    return {
      points,
      maxValue,
      tickValues,
      creditPoints,
      debitPoints,
      savingsPoints,
      passiveIncomePoints,
      creditPath: toPath(creditPoints),
      debitPath: toPath(debitPoints),
      savingsPath: toPath(savingsPoints),
      passiveIncomePath: toPath(passiveIncomePoints),
      innerHeight,
    };
  }, [rows, rangeScale]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Income, Expenditure Tracking</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / <Link to="/Reports">Reports</Link> / Income, Expenditure Tracking
          </p>
        </div>
      </header>

      {error && <p className="notice notice-error">{error}</p>}
      {loading && <p className="notice notice-muted">Loading income and expenditure tracking...</p>}

      {!loading && (
        <section className="panel">
          <div className="chart-toolbar" aria-label="Income and expenditure chart scale">
            <div className="segmented-control">
              {rangeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`segmented-control-button${rangeScale === option.value ? " is-active" : ""}`}
                  onClick={() => setRangeScale(option.value)}
                  aria-pressed={rangeScale === option.value}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {chartData.points.length === 0 ? (
            <p className="notice notice-muted">No reporting period data found.</p>
          ) : (
            <>
              <div className="line-chart-legend">
                <span className="line-chart-legend-item"><span className="line-chart-dot line-chart-dot-credit" /> Total credits</span>
                <span className="line-chart-legend-item"><span className="line-chart-dot line-chart-dot-debit" /> Debits excluding savings</span>
                <span className="line-chart-legend-item"><span className="line-chart-dot line-chart-dot-savings" /> Total savings</span>
                <span className="line-chart-legend-item"><span className="line-chart-dot line-chart-dot-passive" /> Passive income</span>
              </div>

              <div className="table-scroll">
                <svg
                  className="line-chart"
                  viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                  role="img"
                  aria-label="Line chart showing total credits, debits excluding savings, total savings, and passive income for each reporting period"
                >
                  {chartData.tickValues.map((tick) => {
                    const y = chartHeight - chartPadding.bottom - (chartData.maxValue > 0 ? (tick / chartData.maxValue) * chartData.innerHeight : 0);
                    return (
                      <g key={tick}>
                        <line className="line-chart-grid" x1={chartPadding.left} y1={y} x2={chartWidth - chartPadding.right} y2={y} />
                        <text className="line-chart-axis-text" x={chartPadding.left - 10} y={y + 4} textAnchor="end">
                          {formatNumber(tick)}
                        </text>
                      </g>
                    );
                  })}

                  <line className="line-chart-axis" x1={chartPadding.left} y1={chartPadding.top} x2={chartPadding.left} y2={chartHeight - chartPadding.bottom} />
                  <line className="line-chart-axis" x1={chartPadding.left} y1={chartHeight - chartPadding.bottom} x2={chartWidth - chartPadding.right} y2={chartHeight - chartPadding.bottom} />

                  <path className="line-chart-path line-chart-path-credit" d={chartData.creditPath} />
                  <path className="line-chart-path line-chart-path-debit" d={chartData.debitPath} />
                  <path className="line-chart-path line-chart-path-savings" d={chartData.savingsPath} />
                  <path className="line-chart-path line-chart-path-passive" d={chartData.passiveIncomePath} />

                  {chartData.creditPoints.map((point, index) => (
                    <circle key={`credit-${chartData.points[index].label}`} className="line-chart-point line-chart-point-credit" cx={point.x} cy={point.y} r="4" />
                  ))}
                  {chartData.debitPoints.map((point, index) => (
                    <circle key={`debit-${chartData.points[index].label}`} className="line-chart-point line-chart-point-debit" cx={point.x} cy={point.y} r="4" />
                  ))}
                  {chartData.savingsPoints.map((point, index) => (
                    <circle key={`savings-${chartData.points[index].label}`} className="line-chart-point line-chart-point-savings" cx={point.x} cy={point.y} r="4" />
                  ))}
                  {chartData.passiveIncomePoints.map((point, index) => (
                    <circle key={`passive-${chartData.points[index].label}`} className="line-chart-point line-chart-point-passive" cx={point.x} cy={point.y} r="4" />
                  ))}

                  {chartData.points.map((point, index) => {
                    const showLabel =
                      index === 0 ||
                      index === chartData.points.length - 1 ||
                      index % 5 === 0;
                    if (!showLabel) return null;
                    return (
                      <text
                        key={`label-${point.label}`}
                        className="line-chart-axis-text"
                        x={chartData.creditPoints[index].x}
                        y={chartHeight - 16}
                        textAnchor="middle"
                      >
                        {point.label}
                      </text>
                    );
                  })}
                </svg>
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
};

export default ReportsIncomeExpenditureTracking;
